import { ILoginActions, IVenueActions, VenueActionNames } from 'shared-types/ActionsTypes';
import { IGeneralSettingsCache } from './types';
import PouchLoadService from 'external-services/pouch-load-service/index';

const NS = 'GeneralSettingsService';

class GeneralSettingsService {
  private settingsCache: IGeneralSettingsCache = null;
  LoginActions: ILoginActions;
  VenueActions: IVenueActions;

  public init(LoginActions: ILoginActions, VenueActions: IVenueActions): void {
    this.LoginActions = LoginActions;
    this.VenueActions = VenueActions;
    this.LoginActions.handleLogout(() => this.deleteCache());
    this.VenueActions.handleVenueAction(VenueActionNames.VENUE_CHANGE, () => this.deleteCache());
    this.load();
  }

  public _testSetup(): void {
    this.load();
  }

  /**
   * Returns the general setting object via a promise
   * @return {Promise}
   */
  public get(): Promise<IGeneralSettingsCache> {
    return new Promise((resolve: any) => {
      if (this.settingsCache) {
        resolve(this.settingsCache);
      } else {
        this.load().then((settings: IGeneralSettingsCache) => {
          this.setDiaryRolloverDefault(settings);
          // show the new staff initial changes if toggled on in config
          if (window.config?.enableAdvancedStaffSettings) {
            this.checkForMandatoryStaffEditOptions(settings);
          }
          resolve(settings);
        });
      }
    });
  }

  // this function is to set the default values for the staff edit options if the staff edit is enabled and mandatory
  private checkForMandatoryStaffEditOptions(settings: IGeneralSettingsCache){
    if(settings.staffEditEnabled && settings.staffEditRequired && !settings.staffEdit) {
     settings.staffEdit = {
        staffEditAll: true,
        staffEditQuickEdit: true,
        staffEditPaxEdit: true,
        staffEditTableEdit: true,
        staffEditTimeEdit: true,
        staffEditPaymentBOEdit: true,
        staffEditNotesEdit: true,
        staffEditStatusEdit: true
      };
    }
  }

  // This is basically the same as `get`, but it assumes the result has already been cached
  public getSettingsCache(): IGeneralSettingsCache {
    return this.settingsCache;
  }

  /**
   * Saves the settings
   * @return {Promise}
   */
  public save(settings: IGeneralSettingsCache): Promise<IGeneralSettingsCache> {
    return Promise.resolve(
      PouchLoadService.getDbLocal()
        .put(settings)
        .then((res: { rev: string }) => {
          // update revision
          settings._rev = res.rev;

          this.settingsCache = settings;
          return this.settingsCache;
        })
    );
  }

  // ############################################ Private methods ##################################
  private load(): Promise<IGeneralSettingsCache> {
    return new Promise((resolve, reject) => {
      const db = PouchLoadService.getDbLocal();
      db &&
        db
          .get('general_settings')
          .then((settings: unknown) => {
            this.initDefaultValue(settings);
            this.settingsCache = settings as IGeneralSettingsCache;
            resolve(this.settingsCache);
          })
          .catch(() => {
            // the settings have not been defined use a defaut settings
            this.settingsCache = {
              _id: 'general_settings',
              highlightOverdue: true,
              highlightOverdueMins: 15,
              highlightConflicts: true,
              highlightConflictsMins: 15,
            };
            this.initDefaultValue(this.settingsCache);
            resolve(this.settingsCache);
          });
    });
  }

  private initDefaultValue(settings: IGeneralSettingsCache): void {
    // By default, the enableNewBookingNotification flag will be true
    if (settings.enableNewBookingNotification === undefined) {
      settings.enableNewBookingNotification = true;
    }

    // By default, the enableNewSBLBookingNotification flag will be true
    if (settings.enableNewSBLBookingNotification === undefined) {
      settings.enableNewSBLBookingNotification = true;
    }
  }
  
  // enables the diary page refresh behaviour - the corresponding checkbox will be checked by default.
  private setDiaryRolloverDefault(settings: IGeneralSettingsCache): void {
    if (settings.diaryRollover === undefined) {
      settings.diaryRollover = true;
    }
    // if (settings.enableMaxOccupancy) {
    //   settings.enableMaxOccupancy = false; // Disabling Max occupancy till Widget changes are done
    // }

    // By default, the end booking time will be displayed on the diary
    if (settings.enableEndBookingTime === undefined) {
      settings.enableEndBookingTime = true;
    }

    // By default, the highlightMaxCoversPerInterval flag will be true
    if (settings.highlightMaxCoversPerInterval === undefined) {
      settings.highlightMaxCoversPerInterval = true;
    }
  }

  private deleteCache(): void {
    this.settingsCache = null;
  }
}

const instance = new GeneralSettingsService();
export default instance as GeneralSettingsService;
